import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { ActivityType, ChartScanActivity } from '@/lib/api/company/chartScan'
import { getActivityIcon } from '@/modules/drivers/helpers/ActivityIconResolver'
import { parseChartIso } from '@/modules/imports/components/TachographChartCanvas'
import { activityLabel, fmt, useChartT } from '../i18n'

// Same icon set used by the driver-module daily activity DataGrid (see
// modules/drivers/helpers/ActivityIconResolver.ts) — bed, id-badge, hammer,
// truck, question-circle. Keeping these in sync gives users a consistent visual
// vocabulary across the import editor and the rest of the app.
// Labels live in ACTIVITY_LABEL_KEYS (see ../i18n); only the colours are local.
const TYPE_COLOR: Record<number, string> = {
  [ActivityType.Driving]: 'rgb(218, 37, 29)',
  [ActivityType.Work]: 'rgb(231, 120, 23)',
  [ActivityType.Availability]: 'rgb(255, 218, 0)',
  [ActivityType.Rest]: 'rgb(117, 197, 240)',
  [ActivityType.NoData]: '#94a3b8',
}

type Props = {
  activities: ChartScanActivity[]
  noonShiftMinutes: number
  hoveredActivityId: number | null
  hoverFromCanvas: boolean
  selectedActivityIds: Set<number>
  onHover: (id: number | null) => void
  onSelect: (ids: number[], shift: boolean, ctrl: boolean) => void
  onContextMenu: (activity: ChartScanActivity, screenXY: { x: number; y: number }) => void
  // Set of ISO timestamps where a vehicle change occurs; used to render the
  // "Zmiana samochodu" column with the new vehicle's label.
  vehicleChangeAtIso?: Map<string, string>
}

// WPF SuperTacho/Views/Import/TachographChartImportView.xaml:397-429.
// Columns: type icon, from, to, duration, ferry, vehicle change.
// Hover-sync state lifted into the parent (mirrors
// modules/drivers/tabs/activities/components/charts/daily/ActivityChartWrapper.tsx:48-107).
export function ActivityListTable({
  activities,
  noonShiftMinutes,
  hoveredActivityId,
  hoverFromCanvas,
  selectedActivityIds,
  onHover,
  onSelect,
  onContextMenu,
  vehicleChangeAtIso,
}: Props) {
  const T = useChartT()
  const rowsRef = React.useRef<Map<number, HTMLTableRowElement>>(new Map())

  // When the chart hovers an activity, scroll the matching row into view.
  React.useEffect(() => {
    if (hoveredActivityId == null || !hoverFromCanvas) return
    const el = rowsRef.current.get(hoveredActivityId)
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [hoveredActivityId, hoverFromCanvas])

  // 1:1 with WPF TachographChartActivityDurationToolTipConverter: when 2+ rows
  // are selected, the title attribute aggregates per-type durations.
  const selectionSummary = React.useMemo(() => {
    if (selectedActivityIds.size < 2) return ''
    const totals = new Map<number, number>()
    for (const a of activities) {
      if (!selectedActivityIds.has(a.activityId)) continue
      const ms = +new Date(a.endTime) - +new Date(a.startTime)
      totals.set(a.type, (totals.get(a.type) ?? 0) + Math.max(0, ms))
    }
    return Array.from(totals.entries())
      .map(([t, ms]) => `${activityLabel(T, t)}: ${formatHM(ms)}`)
      .join(' · ')
  }, [activities, selectedActivityIds, T])

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-rounded tw-border tw-border-slate-200 tw-bg-white">
      <div className="tw-border-b tw-border-slate-200 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
        {fmt(T('activitiesCount', 'Aktywności ({0})'), activities.length)}
        {selectionSummary && (
          <span className="tw-ml-2 tw-normal-case tw-text-slate-700">{selectionSummary}</span>
        )}
      </div>
      <div className="tw-flex-1 tw-overflow-auto">
        <table className="tw-w-full tw-text-sm">
          <thead className="tw-sticky tw-top-0 tw-bg-slate-50 tw-text-xs tw-uppercase tw-text-slate-500">
            <tr>
              <th className="tw-px-2 tw-py-1 tw-text-left">{T('colActivity', 'Aktywność')}</th>
              <th className="tw-px-2 tw-py-1 tw-text-left">{T('colFrom', 'Data od')}</th>
              <th className="tw-px-2 tw-py-1 tw-text-left">{T('colTo', 'Data do')}</th>
              <th className="tw-px-2 tw-py-1 tw-text-left">{T('colDuration', 'Czas trwania')}</th>
              <th className="tw-px-2 tw-py-1 tw-text-left">{T('colFerry', 'Prom')}</th>
              <th className="tw-px-2 tw-py-1 tw-text-left">
                {T('colVehicleChange', 'Zmiana samochodu')}
              </th>
            </tr>
          </thead>
          <tbody>
            {activities.map((a, idx) => {
              const color = TYPE_COLOR[a.type] ?? TYPE_COLOR[ActivityType.Rest]
              const isHover = hoveredActivityId === a.activityId
              const isSelected = selectedActivityIds.has(a.activityId)
              return (
                <tr
                  key={`act-${idx}-${a.activityId}-${a.startTime}`}
                  ref={(el) => {
                    if (el) rowsRef.current.set(a.activityId, el)
                  }}
                  className={[
                    'tw-cursor-pointer tw-border-b tw-border-slate-100',
                    isHover ? 'tw-bg-blue-50' : '',
                    isSelected ? 'tw-bg-blue-100' : 'hover:tw-bg-slate-50',
                  ].join(' ')}
                  onMouseEnter={() => onHover(a.activityId)}
                  onMouseLeave={() => onHover(null)}
                  onClick={(e) => onSelect([a.activityId], e.shiftKey, e.ctrlKey || e.metaKey)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    onContextMenu(a, { x: e.clientX, y: e.clientY })
                  }}
                >
                  <td className="tw-whitespace-nowrap tw-px-2 tw-py-1">
                    <FontAwesomeIcon
                      icon={getActivityIcon(a.type) as IconProp}
                      className="tw-mr-2"
                      style={{ color }}
                    />
                    <span className="tw-text-xs tw-text-slate-600">
                      {activityLabel(T, a.type)}
                    </span>
                  </td>
                  <td className="tw-px-2 tw-py-1 tw-font-mono">
                    {hhmmShifted(a.startTime, noonShiftMinutes)}
                  </td>
                  <td className="tw-px-2 tw-py-1 tw-font-mono">
                    {hhmmShifted(a.endTime, noonShiftMinutes)}
                  </td>
                  <td className="tw-px-2 tw-py-1 tw-font-mono">
                    {duration(a.startTime, a.endTime)}
                  </td>
                  <td className="tw-px-2 tw-py-1">
                    {a.ferryTrainCrossing ? T('yes', 'Tak') : T('no', 'Nie')}
                  </td>
                  <td className="tw-px-2 tw-py-1 tw-text-xs tw-text-slate-600">
                    {vehicleChangeAtIso?.get(a.startTime) ?? ''}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function hhmmShifted(iso: string, shiftMinutes: number): string {
  const d = new Date(parseChartIso(iso).getTime() + shiftMinutes * 60_000)
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}
function duration(startIso: string, endIso: string): string {
  // Wrap-merged activities (cross-midnight rest fused with morning rest) have
  // EndTime < StartTime in real-calendar terms; treat that as +24h so the
  // duration column shows the actual length, not 00:00 (1:1 with WPF
  // EffectiveDuration).
  let ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  if (ms < 0) ms += 24 * 60 * 60 * 1000
  return formatHM(ms)
}

function formatHM(ms: number): string {
  const total = Math.round(ms / 60_000)
  const h = Math.floor(total / 60)
  const m = total - h * 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
